import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WordsProvider } from '../clients/words-ui';
import { useFilialShapki } from '../multi-tenant/hooks';
import { useRazborLida } from '../doski/hooks';
import { slovoOtkaza } from '../doski/api';
import type { DealReference } from '../doski/amo-api';

/**
 * ЦЕНТР ОКНА КЛИЕНТКИ (`s681`): лид или запись, нажатые сбоку, встают ПОВЕРХ
 * текущего экрана — страница не меняется; «назад» (крестик, Escape, фон)
 * снимает верхнее окно и возвращает туда, откуда пришли. Окна складываются
 * стопкой: переписка → лид → его запись → назад к лиду → назад к переписке.
 *
 * ⛔ ОКНА — ТЕ ЖЕ, ЧТО НА СВОИХ ЭКРАНАХ: карточка лида доски (`KartochkaLida`)
 * и карточка записи расписания (`ZapisKartochkaPage` в режиме «поверх»).
 * Второго вида лида или записи не заводим (§31.8).
 */

type Centr = { tip: 'lid'; id: number } | { tip: 'zapis'; id: number } | { tip: 'deal'; id: number; reference: DealReference };

type OknoCentr = {
  otkrytLid: (id: number) => void;
  otkrytZapis: (id: number) => void;
  otkrytKlientku: (id: number) => void;
  otkrytSdelku: (reference: DealReference) => void;
};

const Ctx = createContext<OknoCentr | null>(null);

// The public hook shares this provider's context; keeping it here preserves all existing window callers.
// eslint-disable-next-line react-refresh/only-export-components
export function useOknoCentr(): OknoCentr | null {
  return useContext(Ctx);
}

const ZapisPoverh = lazy(() => import('../../pages/ZapisKartochkaPage').then((m) => ({ default: m.ZapisKartochkaPage })));
const KartochkaLidaLenivo = lazy(() => import('../doski/components/KartochkaLida').then((m) => ({ default: m.KartochkaLida })));
const SdelkaPoverh = lazy(() => import('../../pages/AmoSdelkiPage').then((m) => ({ default: m.DealWorkWindow })));

export function OknoKlientkiProvider({ children }: { children: ReactNode }) {
  const [stopka, setStopka] = useState<Centr[]>([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const prezhniy = useRef(pathname);

  // Ушли на другой экран — стопка окон остаётся там, где её открыли.
  useEffect(() => {
    if (prezhniy.current !== pathname) {
      prezhniy.current = pathname;
      setStopka([]);
    }
  }, [pathname]);

  const otkrytLid = useCallback((id: number) => setStopka((s) => [...s, { tip: 'lid', id }]), []);
  const otkrytZapis = useCallback((id: number) => setStopka((s) => [...s, { tip: 'zapis', id }]), []);
  const otkrytKlientku = useCallback((id: number) => navigate(`/clients/${id}`), [navigate]);
  const otkrytSdelku = useCallback((reference: DealReference) => {
    const id = reference.kind === 'amo_native_deals' ? reference.local_id : reference.original_amo_id;
    if (id !== null) setStopka((s) => [...s, { tip: 'deal', id, reference }]);
  }, []);
  const nazad = useCallback(() => setStopka((s) => s.slice(0, -1)), []);
  const znachenie = useMemo(() => ({ otkrytLid, otkrytZapis, otkrytKlientku, otkrytSdelku }), [otkrytLid, otkrytZapis, otkrytKlientku, otkrytSdelku]);

  return (
    <Ctx.Provider value={znachenie}>
      {children}
      {stopka.map((c, i) => (
        <div key={`${c.tip}-${c.id}-${i}`} style={{ position: 'relative', zIndex: 200 + i * 10 }} data-okno-centr={c.tip}>
          <Suspense fallback={null}>
            {c.tip === 'lid' ? (
              <OknoLida id={c.id} onZakryt={nazad} onOtkryt={otkrytLid} />
            ) : c.tip === 'deal' ? (
              <SdelkaPoverh reference={c.reference} onClose={nazad} />
            ) : (
              <ZapisPoverh poverh={{ id: c.id, onZakryt: nazad }} />
            )}
          </Suspense>
        </div>
      ))}
    </Ctx.Provider>
  );
}

function OknoLida({ id, onZakryt, onOtkryt }: { id: number; onZakryt: () => void; onOtkryt: (id: number) => void }) {
  const razbor = useRazborLida(id);
  const filialId = useFilialShapki();

  return (
    <WordsProvider hints={{}} words={razbor.data?.words ?? { labels: {}, texts: {} }}>
      <KartochkaLidaLenivo dannye={razbor.data} gruzitsya={razbor.isLoading} otkazZagruzki={razbor.isError ? slovoOtkaza(razbor.error) : null} onPovtorit={() => void razbor.refetch()} onZakryt={onZakryt} onOtkryt={onOtkryt} filialVidim={filialId === null} />
    </WordsProvider>
  );
}
